import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import SlateDialog from 'components/Shared/SlateDialog/SlateDialog';
import { addAccount } from 'store/actions/account';
import { entityNames } from 'config/constants';
import './style.css';

var CODE_LENGTH = 6;

class AddStackDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      code: ['', '', '', '', '', ''],
      isSubmitting: false,
      error: '',
    };
    this.inputRefs = [];
    for (var i = 0; i < CODE_LENGTH; i++) {
      this.inputRefs.push(React.createRef());
    }
  }

  componentDidUpdate(prevProps) {
    var currentAccount = this.props.currentAccount;
    var existingAccountUUID = this.props.existingAccountUUID;
    var onClose = this.props.onClose;
    var onSwitchAccount = this.props.onSwitchAccount;
    var entities = this.props.entities;

    // Switch to new account when account changes (successful add)
    if (prevProps.currentAccount && currentAccount &&
        prevProps.currentAccount.uuid !== currentAccount.uuid) {
      this.resetForm();
      onSwitchAccount(currentAccount.uuid);
    }

    // Switch to existing account if user tried to add one they already have
    if (existingAccountUUID &&
        prevProps.existingAccountUUID !== existingAccountUUID) {
      onSwitchAccount(existingAccountUUID);
      this.resetForm();
      onClose();
    }

    // Check for API errors
    var prevEntity = prevProps.entities && prevProps.entities[entityNames.ADD_ACCOUNT];
    var currentEntity = entities && entities[entityNames.ADD_ACCOUNT];
    if (currentEntity && currentEntity.hasErrors &&
        (!prevEntity || !prevEntity.hasErrors)) {
      this.setState({
        isSubmitting: false,
        error: this.props.t('messages:invalidAuthCode')
      });
    }

    // Reset submitting state when fetch completes
    if (prevEntity && prevEntity.isFetching && currentEntity && !currentEntity.isFetching) {
      this.setState({ isSubmitting: false });
    }
  }

  resetForm = () => {
    this.setState({
      code: ['', '', '', '', '', ''],
      isSubmitting: false,
      error: '',
    });
  };

  handleOpen = () => {
    // Focus first input when dialog opens
    var _this = this;
    setTimeout(function() {
      if (_this.inputRefs[0] && _this.inputRefs[0].current) {
        _this.inputRefs[0].current.focus();
      }
    }, 100);
  };

  handleInputChange = (index, event) => {
    var value = event.target.value.toUpperCase();
    var newCode = this.state.code.slice();

    // Clear error when user starts typing
    if (this.state.error) {
      this.setState({ error: '' });
    }

    // Handle paste of full code
    if (value.length > 1) {
      var pastedChars = value.replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
      for (var i = 0; i < CODE_LENGTH; i++) {
        newCode[i] = pastedChars[i] || '';
      }
      this.setState({ code: newCode });

      // Focus last filled input or submit if complete
      var lastFilledIndex = Math.min(pastedChars.length - 1, CODE_LENGTH - 1);
      if (pastedChars.length >= CODE_LENGTH) {
        this.inputRefs[CODE_LENGTH - 1].current.focus();
        // Auto-submit after paste
        var _this = this;
        setTimeout(function() {
          _this.handleSubmit();
        }, 100);
      } else if (this.inputRefs[lastFilledIndex + 1]) {
        this.inputRefs[lastFilledIndex + 1].current.focus();
      }
      return;
    }

    // Single character input
    var char = value.replace(/[^A-Z0-9]/g, '').slice(0, 1);
    newCode[index] = char;
    this.setState({ code: newCode });

    // Auto-advance to next input
    if (char && index < CODE_LENGTH - 1) {
      this.inputRefs[index + 1].current.focus();
    }

    // Auto-submit when all filled
    if (char && index === CODE_LENGTH - 1) {
      var allFilled = newCode.every(function(c) { return c !== ''; });
      if (allFilled) {
        setTimeout(function() {
          _this.handleSubmit();
        }, 100);
      }
    }
  };

  handleKeyDown = (index, event) => {
    // Handle backspace
    if (event.key === 'Backspace') {
      var newCode = this.state.code.slice();

      if (newCode[index]) {
        // Clear current input
        newCode[index] = '';
        this.setState({ code: newCode });
      } else if (index > 0) {
        // Move to previous input and clear it
        newCode[index - 1] = '';
        this.setState({ code: newCode });
        this.inputRefs[index - 1].current.focus();
      }
      event.preventDefault();
    }

    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      this.inputRefs[index - 1].current.focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      this.inputRefs[index + 1].current.focus();
      event.preventDefault();
    }

    // Handle Enter
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  };

  handleFocus = (index) => {
    // Select content on focus
    if (this.inputRefs[index] && this.inputRefs[index].current) {
      this.inputRefs[index].current.select();
    }
  };

  handleSubmit = () => {
    var code = this.state.code;
    var fullCode = code.join('');

    if (fullCode.length !== CODE_LENGTH) {
      this.setState({ error: this.props.t('messages:invalidAuthCode') });
      return;
    }

    this.setState({ isSubmitting: true, error: '' });
    this.props.addAccount({ authCode: fullCode }, entityNames.ADD_ACCOUNT);
  };

  handleClose = () => {
    this.resetForm();
    this.props.onClose();
  };

  render() {
    var _this = this;
    var props = this.props;
    var state = this.state;
    var open = props.open;
    var t = props.t;
    var code = state.code;
    var isSubmitting = state.isSubmitting;
    var error = state.error;
    var isComplete = code.every(function(c) { return c !== ''; });

    return (
      <SlateDialog
        open={open}
        onClose={this.handleClose}
        title={t('stackSettings:addStackToProfile')}
        maxWidth="xs"
        TransitionProps={{ onEntered: this.handleOpen }}
      >
        <div className="add-stack-dialog-content">
          <div className="add-stack-dialog-description">
            {t('stackSettings:enterAStackAuthCode')}
          </div>

          <div className="auth-code-input-container">
            <div className="auth-code-label">{t('stackSettings:authCode')}</div>
            <div className="auth-code-input-boxes">
              {code.map(function(char, index) {
                return (
                  <input
                    key={index}
                    ref={_this.inputRefs[index]}
                    type="text"
                    maxLength={CODE_LENGTH}
                    value={char}
                    onChange={function(e) { _this.handleInputChange(index, e); }}
                    onKeyDown={function(e) { _this.handleKeyDown(index, e); }}
                    onFocus={function() { _this.handleFocus(index); }}
                    className={'auth-code-input-box' + (char ? ' auth-code-input-box--filled' : '') + (error ? ' auth-code-input-box--error' : '')}
                    autoComplete="off"
                    spellCheck="false"
                    disabled={isSubmitting}
                  />
                );
              })}
            </div>

            {error && (
              <div className="auth-code-error">
                <i className="material-symbols-outlined">error</i>
                {error}
              </div>
            )}
          </div>

          <button
            type="button"
            className={'add-stack-submit-btn' + (isComplete && !isSubmitting ? ' add-stack-submit-btn--ready' : '')}
            onClick={this.handleSubmit}
            disabled={!isComplete || isSubmitting}
          >
            {isSubmitting ? (
              <span className="add-stack-submit-loading">
                <i className="material-symbols-outlined add-stack-spinner">progress_activity</i>
                {t('stackSettings:addStack')}
              </span>
            ) : (
              t('stackSettings:addStack')
            )}
          </button>
        </div>
      </SlateDialog>
    );
  }
}

AddStackDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSwitchAccount: PropTypes.func.isRequired,
  currentAccount: PropTypes.object,
  existingAccountUUID: PropTypes.string,
  addAccount: PropTypes.func.isRequired,
  entities: PropTypes.object,
  t: PropTypes.func.isRequired,
};

var mapStateToProps = function (state) {
  return {
    currentAccount: state.accounts.currentAccount,
    existingAccountUUID: state.accounts.existingAccountUUID,
    entities: state.entities,
  };
};

export default withTranslation()(
  connect(mapStateToProps, { addAccount })(AddStackDialog)
);
